import { useState, useEffect, useCallback } from 'react';
import { contactService } from '../services/domain/contactService';
import type { Contact } from '../types/contacts';

interface UseContactsResult {
  data: Contact[];
  loading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  refetch: () => void;
  updating: boolean;
  searching: boolean;
  searchLeadById: (id: number) => Promise<{ success: boolean, message?: string }>;
}

interface ContactParams {
  status_persona_id?: number;
  page?: number;
  name_persona?: string;
  pageSize?: number,
  tipo_persona_id?: string,
  searchTerm?: string;
  agentId?: string;
  sourceId?: string;
  statusId?: string;
}

export const useContacts = (params: ContactParams): UseContactsResult => {
  const [data, setData] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [updating] = useState(false);
  const [searching] = useState(false);


  const fetchContact = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Construir parámetros de búsqueda
      const searchParams: any = {
        page: params.page,
        pageSize: params.pageSize
      };

      // Agregar filtros solo si tienen valor
      if (params.searchTerm) {
        searchParams.searchTerm = params.searchTerm;
      }
      if (params.agentId) {
        searchParams.user_id = params.agentId;
      }
      if (params.sourceId) {
        searchParams.lead_source_id = params.sourceId;
      }
      if (params.statusId) {
        searchParams.lead_status_id = params.statusId;
      }
      if (params.name_persona) {
        searchParams.name_persona = params.name_persona;
      }
      if (params.status_persona_id) {
        searchParams.status_persona_id = params.status_persona_id;
      }
      if (params.tipo_persona_id) {
        searchParams.tipo_persona_id = params.tipo_persona_id;
      }

      const response = await contactService.getContacts(searchParams);
      
      setData(response.contacts);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    params.page, 
    params.pageSize,
    params.searchTerm,
    params.agentId,
    params.sourceId,
    params.statusId,
    params.status_persona_id, 
    params.name_persona,
    params.tipo_persona_id
  ]);

  const searchLeadById = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      return ({success: true})
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return ({success: false})
    } finally {
      setLoading(false);
    }
  }, [fetchContact]);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null);
    }
    catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en actualizar';
      setError(errorMessage);
    } finally {
      setLoading(false)
    }
  }, [fetchContact]);



  return {
    data,
    loading,
    error,
    total,
    totalPages,
    refetch,
    updating,
    searching,
    searchLeadById
  };
};